import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tv, Plus, MoreVertical, Copy, Archive, ArchiveRestore, Pencil, Globe, Search, LayoutGrid, List, Users, Sparkles, Trash2, AlertTriangle, Youtube } from "lucide-react";
import { CreateByAIModal } from "@/components/CreateByAIModal";

const LANG_NAMES = {
  "en-US": "English (US)", "en-GB": "English (UK)", "fr-FR": "French", "es-US": "Spanish (US)",
  "es-ES": "Spanish (Spain)", "de-DE": "German", "pt-BR": "Portuguese (BR)", "az-AZ": "Azerbaijani",
  "tr-TR": "Turkish", "ru-RU": "Russian", "ar-SA": "Arabic", "hi-IN": "Hindi", "ja-JP": "Japanese",
};
const langLabel = (l) => `${LANG_NAMES[l] || l} (${l})`;

function ChannelCard({ c, navigate, doDuplicate, doArchive, onDelete }) {
  const yb = c.youtube_binding || { connected: false, health: "none" };
  const ytState = yb.health === "connected" ? "connected" : yb.health === "expired" ? "expired" : "none";
  const borderCls = ytState === "connected"
    ? "border-2 border-emerald-500/70 hover:border-emerald-400"
    : ytState === "expired"
      ? "border-2 border-amber-500/70 hover:border-amber-400"
      : "border-2 border-rose-500/60 hover:border-rose-400";
  return (
    <div data-testid={`channel-card-${c.id}`} data-yt-state={ytState}
      className={`bg-[#111827] rounded-xl p-5 transition-colors ${borderCls} ${c.archived ? "opacity-70" : ""}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-none border border-slate-700"
            style={{ background: c.data.brand_colors?.[0] || "#1E293B" }}>
            <Tv className="w-4 h-4 text-white/90" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-slate-100 leading-tight">{c.data.name}</h3>
            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 mt-0.5" data-testid={`channel-lang-${c.id}`}>
              <Globe className="w-3 h-3" /> {c.data.primary_language}
            </span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-slate-500 hover:text-slate-300 p-1" data-testid={`channel-menu-${c.id}`}><MoreVertical className="w-4 h-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#111827] border-slate-700 text-slate-200">
            <DropdownMenuItem onClick={() => navigate(`/channels/${c.id}`)} className="cursor-pointer focus:bg-slate-800" data-testid={`channel-edit-${c.id}`}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doDuplicate(c.id)} className="cursor-pointer focus:bg-slate-800" data-testid={`channel-duplicate-${c.id}`}><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
            {c.archived ? (
              <DropdownMenuItem onClick={() => doArchive(c.id, false)} className="cursor-pointer focus:bg-slate-800" data-testid={`channel-restore-${c.id}`}><ArchiveRestore className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => doArchive(c.id, true)} className="cursor-pointer text-amber-400 focus:bg-slate-800" data-testid={`channel-archive-${c.id}`}><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-slate-800" />
            <DropdownMenuItem onClick={() => onDelete(c)} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300" data-testid={`channel-delete-${c.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete channel…</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mb-3">
        <p className="text-[10px] uppercase tracking-wide text-slate-600 mb-0.5">Niche / topic</p>
        <p className="text-sm text-slate-200 font-medium line-clamp-2 min-h-[40px]" data-testid={`channel-niche-${c.id}`}>{c.data.niche || "No niche set"}</p>
      </div>
      {c.data.target_audience && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5 mb-3"><Users className="w-3 h-3" /> {c.data.target_audience}</p>
      )}

      <div className="mb-3" data-testid={`channel-yt-${c.id}`}>
        {ytState === "connected" ? (
          <a href={yb.youtube_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 hover:underline"
            data-testid={`channel-yt-link-${c.id}`}>
            <Youtube className="w-3.5 h-3.5 text-rose-400 flex-none" />
            <span className="truncate">YouTube: {yb.youtube_channel_title || "(untitled)"} ({yb.youtube_channel_id})</span>
          </a>
        ) : ytState === "expired" ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-300" data-testid={`channel-yt-expired-${c.id}`}>
            <Youtube className="w-3.5 h-3.5 flex-none" /> YouTube: Token expired — reconnect
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-rose-300/90" data-testid={`channel-yt-unlinked-${c.id}`}>
            <Youtube className="w-3.5 h-3.5 flex-none" /> YouTube: bağlı deyil
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {c.is_sample && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/30" data-testid={`sample-badge-${c.id}`}>Sample</span>}
        <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">v{c.version}</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">{c.data.default_format} · {c.data.aspect_ratio}</span>
        {c.archived && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">Archived</span>}
      </div>
    </div>
  );
}

export default function Channels() {
  const navigate = useNavigate();
  const { loadChannels } = useStudio();
  const [channels, setChannels] = useState(null);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState("group"); // group | list
  const [langFilter, setLangFilter] = useState("all");
  const [nicheQuery, setNicheQuery] = useState("");
  const [showAI, setShowAI] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // channel obj
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [trashCount, setTrashCount] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [chRes, trRes] = await Promise.all([
        api.get(`/channels?include_archived=${showArchived}`),
        api.get("/channels/trash"),
      ]);
      setChannels(chRes.data);
      setTrashCount(trRes.data.count || 0);
    } catch (e) { setError(apiError(e)); }
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  const doDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/channels/${id}/duplicate`);
      toast.success("Channel duplicated (credentials excluded)");
      await loadChannels();
      navigate(`/channels/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const doArchive = async (id, archived) => {
    try {
      await api.post(`/channels/${id}/archive`, { archived });
      toast.success(archived ? "Channel archived" : "Channel restored");
      await Promise.all([load(), loadChannels()]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDelete = async (c) => {
    setDeleteTarget(c); setConfirmName(""); setPreview(null);
    try {
      const { data } = await api.get(`/channels/${c.id}/delete-preview`);
      setPreview(data.counts);
    } catch (e) {
      // Preview is advisory; the delete dialog still works without counts.
      logger.error("Delete-preview failed", e);
    }
  };
  const undoDelete = async (id, name) => {
    try {
      await api.post(`/channels/${id}/undo-delete`);
      toast.success(`Restored "${name}"`);
      await Promise.all([load(), loadChannels()]);
    } catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const id = deleteTarget.id, name = deleteTarget.data?.name;
    try {
      await api.delete(`/channels/${id}`, { data: { confirm_name: confirmName } });
      setDeleteTarget(null);
      await Promise.all([load(), loadChannels()]);
      toast.success(`Channel "${name}" deleted`, {
        duration: 60000,
        description: "Permanent in 60s.",
        action: { label: "Undo", onClick: () => undoDelete(id, name) },
      });
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };

  const languages = useMemo(
    () => Array.from(new Set((channels || []).map((c) => c.data.primary_language))).sort((a, b) => a.localeCompare(b)),
    [channels]);

  const filtered = useMemo(() => {
    const q = nicheQuery.trim().toLowerCase();
    return (channels || []).filter((c) =>
      (langFilter === "all" || c.data.primary_language === langFilter) &&
      (!q || (c.data.niche || "").toLowerCase().includes(q)));
  }, [channels, langFilter, nicheQuery]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((c) => { (g[c.data.primary_language] = g[c.data.primary_language] || []).push(c); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Foundation</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Channels &amp; Profiles</h1>
          <p className="text-sm text-slate-400 mt-2">One channel per topic/niche. Grouped by language so you can see your coverage at a glance.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-toggle" />
            Show archived
          </label>
          <Button onClick={() => setShowAI(true)} variant="outline" className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10" data-testid="create-with-ai-button">
            <Sparkles className="w-4 h-4 mr-1.5" /> Create with AI
          </Button>
          <Button onClick={() => navigate("/channels/trash")} variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800 relative" data-testid="trash-btn">
            <Trash2 className="w-4 h-4 mr-1.5" /> Zibil
            {trashCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-rose-500 text-white flex items-center justify-center px-1" data-testid="trash-count-badge">
                {trashCount}
              </span>
            )}
          </Button>
          <Button onClick={() => navigate("/channels/new")} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="new-channel-button">
            <Plus className="w-4 h-4 mr-1.5" /> New channel
          </Button>
        </div>
      </div>

      <CreateByAIModal open={showAI} onOpenChange={setShowAI} onCreated={async () => { await Promise.all([load(), loadChannels()]); }} />

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex rounded-lg border border-slate-800 overflow-hidden" data-testid="view-toggle">
          <button onClick={() => setViewMode("group")} data-testid="view-group"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm ${viewMode === "group" ? "bg-violet-600 text-white" : "bg-slate-900 text-slate-400 hover:text-slate-200"}`}>
            <LayoutGrid className="w-4 h-4" /> Group by language
          </button>
          <button onClick={() => setViewMode("list")} data-testid="view-list"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm ${viewMode === "list" ? "bg-violet-600 text-white" : "bg-slate-900 text-slate-400 hover:text-slate-200"}`}>
            <List className="w-4 h-4" /> List
          </button>
        </div>
        <Select value={langFilter} onValueChange={setLangFilter}>
          <SelectTrigger className="w-56 bg-slate-900 border-slate-800 text-slate-200" data-testid="language-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
            <SelectItem value="all">All languages</SelectItem>
            {languages.map((l) => <SelectItem key={l} value={l}>{langLabel(l)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={nicheQuery} onChange={(e) => setNicheQuery(e.target.value)} placeholder="Search niche…"
            className="pl-9 bg-slate-900 border-slate-800 text-slate-100" data-testid="niche-search" />
        </div>
      </div>

      {!channels ? <LoadingState label="Loading channels…" /> : channels.length === 0 ? (
        <EmptyState icon={Tv} title="No channels yet" description="Create your first channel profile to define its voice, brand, format and budget."
          action={<Button onClick={() => navigate("/channels/new")} className="bg-violet-600 hover:bg-violet-500">Create channel</Button>} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title="No channels match your filters" description="Try clearing the language filter or niche search." />
      ) : viewMode === "group" ? (
        <div className="space-y-8" data-testid="grouped-view">
          {grouped.map(([lang, list]) => (
            <div key={lang} data-testid={`group-${lang}`}>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-violet-300" />
                <h2 className="text-sm font-display font-bold text-slate-100">{langLabel(lang)}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/30" data-testid={`group-count-${lang}`}>{list.length} channel{list.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {list.map((c) => <ChannelCard key={c.id} c={c} navigate={navigate} doDuplicate={doDuplicate} doArchive={doArchive} onDelete={openDelete} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="flat-view">
          {filtered.map((c) => <ChannelCard key={c.id} c={c} navigate={navigate} doDuplicate={doDuplicate} doArchive={doArchive} onDelete={openDelete} />)}
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-[#111827] border-red-500/30 text-slate-100" data-testid="delete-channel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300"><AlertTriangle className="w-5 h-5" /> Delete channel permanently</DialogTitle>
            <DialogDescription className="text-slate-400">
              This permanently deletes <span className="font-semibold text-slate-200">{deleteTarget?.data?.name}</span> and everything scoped to it — projects, scripts, renders, ideas, playbooks, growth state, budgets, schedules and channel-only styles. Rendered video files are erased too. <span className="text-red-300 font-medium">This cannot be undone.</span> Global styles are kept.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            {preview && (
              <div className="mb-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5 text-xs text-slate-300" data-testid="delete-preview">
                <p className="text-slate-400 mb-1.5">This will permanently delete:</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span><b className="text-slate-100">{preview.projects}</b> projects</span>
                  <span><b className="text-slate-100">{preview.ideas}</b> ideas</span>
                  <span><b className="text-slate-100">{preview.script_versions}</b> scripts</span>
                  <span><b className="text-slate-100">{preview.scenes}</b> scenes</span>
                  <span><b className="text-slate-100">{preview.renders}</b> renders ({preview.render_storage_mb} MB)</span>
                  <span><b className="text-slate-100">{preview.schedules}</b> schedules</span>
                  <span><b className="text-slate-100">{preview.cost_entries + preview.income_entries}</b> cost/income</span>
                  <span><b className="text-slate-100">{preview.styles_deleted}</b> channel styles</span>
                </div>
                <p className="text-slate-500 mt-1.5">{preview.styles_kept_global} global style(s) kept. You&apos;ll have 60s to undo.</p>
              </div>
            )}
            <label className="text-sm text-slate-300 mb-1.5 block">Type <span className="font-mono text-slate-100">{deleteTarget?.data?.name}</span> to confirm</label>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} autoFocus
              placeholder={deleteTarget?.data?.name} data-testid="delete-confirm-input"
              className="bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-red-500/40" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="cancel-delete-channel">Cancel</Button>
            <Button onClick={doDelete} disabled={deleting || confirmName !== deleteTarget?.data?.name} className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-40" data-testid="confirm-delete-channel">
              {deleting ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
